import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import PackageActions from '../actions/packages';

import { trackEvent } from '../helpers';
import { isMobile, getCurrencySymbol } from '../helpers';

import Button from './Button';
import ExpandableCopy from './ExpandableCopy';
import VendorProfileSection from './VendorProfileSection';
import { useDispatch, useSelector } from 'react-redux';

const Wrapper = styled.div`
  position: relative;
`;

const GridWrapper = styled.div`
  overflow-x: ${p => (p.isScrollable ? 'scroll' : 'auto')};
  width: 99%;
`;

const Grid = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(${p => p.size}, 1fr);
  margin-left: -${p => p.currentPos * (100 / p.maxOnScreen)}%;
  transition: margin 0.3s cubic-bezier(0.64, 1.51, 0.66, 0.9);
  width: ${p => p.size * (100 / p.maxOnScreen)}%;
`;

const Card = styled.div`
  background: #fff;
  border: solid 1px #e7e7e7;
  border-radius: 6px;
  padding-bottom: 20px;
  max-width: 100%;
  overflow: hidden;
`;

const SkeletonCard = styled(Card)`
  padding: 20px;
  min-height: 220px;
  > div {
    background: #f7f7f7;
    display: block;
    margin-bottom: 18px;
    width: 100%;
  }
  > div:nth-child(1) {
    height: 22px;
    width: 80%;
  }
  > div:nth-child(2) {
    background: repeating-linear-gradient(
      #f9f9f9,
      #f9f9f9 15px,
      #fff 15px,
      #fff 21px
    );
    height: 80px;
  }
  > div:nth-child(3) {
    background: #f7f7f7;
    height: 19px;
    width: 80px;
    margin: 30px auto 0;
  }
`;

const PrevNextBtn = styled.div`
  background: ${p => p.theme.primary600};
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  border-radius: 100px;
  height: 44px;
  position: absolute;
  ${p => (p.type === 'next' ? `right` : `left`)}: -10px;
  transform: translateY(-50%);
  transition: box-shadow 0.3s;
  top: 50%;
  width: 44px;

  &:hover {
    box-shadow: 0 16px 18px rgba(0, 0, 0, 0.2);
  }

  &:after {
    border-bottom: solid 3px #fff;
    border-right: solid 3px #fff;
    ${p =>
      p.type === 'next'
        ? `transform: rotate(-45deg) translate(-20%, -80%)`
        : `transform: rotate(135deg) translate(-10%,60%)`};
    content: ' ';
    width: 12px;
    height: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    display: block;
  }
`;

const Name = styled.h3`
  margin: 16px 20px 1em;
`;

const Description = styled.div`
  font-size: 14px;
  margin: 0 20px;
  white-space: pre-line;
`;

const Price = styled.div`
  margin: 12px 20px 0;
  text-align: center;
`;

const Variation = styled.div`
  font-size: 13px;
  color: #888;
  text-transform: uppercase;
  margin: 4px 0;
`;

const ShowPricingButtonWrapper = styled.div`
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export default function Packages({ country, userId, vendorId }) {
  const dispatch = useDispatch();
  const allPackages = useSelector(state => state.packages);

  const isFetching = allPackages.is_fetching;
  const packages = allPackages[vendorId] ?? {};

  const [currentPos, setCurrentPos] = useState(0);
  const [showingPackages, setShowingPackages] = useState(false);
  const maxOnScreen = isMobile ? 1.25 : 3;
  const isScrollable = isMobile;
  const $ = getCurrencySymbol(country);

  useEffect(() => {
    dispatch(PackageActions.fetchPackages(vendorId));
  }, []);

  if ((!isFetching && !packages) || !Object.keys(packages).length) {
    return null;
  }

  if (!showingPackages || isFetching) {
    return (
      <VendorProfileSection title={`Pricing Packages`}>
        <Wrapper>
          <GridWrapper isScrollable={isScrollable}>
            <Grid size={3} currentPos={currentPos} maxOnScreen={maxOnScreen}>
              <SkeletonCard>
                <div />
                <div />
                <div />
              </SkeletonCard>
              <SkeletonCard>
                <div />
                <div />
                <div />
              </SkeletonCard>
              <SkeletonCard>
                <div />
                <div />
                <div />
              </SkeletonCard>
            </Grid>
          </GridWrapper>
          {!isFetching && (
            <ShowPricingButtonWrapper>
              <Button
                type="primary"
                onClick={() => {
                  trackEvent({ event: 'show-packages', userId, vendorId });
                  setShowingPackages(true);
                }}
              >
                Show Packages
              </Button>
            </ShowPricingButtonWrapper>
          )}
        </Wrapper>
      </VendorProfileSection>
    );
  }

  const visiblePackages = Object.keys(packages)
    .map(k => packages[k])
    .filter(o => !!o.name);
  const total = visiblePackages.length;

  return (
    <VendorProfileSection>
      <Wrapper>
        <GridWrapper isScrollable={isScrollable}>
          <Grid size={total} currentPos={currentPos} maxOnScreen={maxOnScreen}>
            {visiblePackages.map(p => {
              if (!p.name) {
                return null;
              }
              return (
                <Card key={p.id}>
                  {p.cover_image && p.cover_image.original && (
                    <img
                      src={p.cover_image.original}
                      style={{ maxWidth: '100%', marginBottom: '6px' }}
                    />
                  )}
                  <Name>{p.name}</Name>
                  {p.description && (
                    <Description>
                      <ExpandableCopy>{p.description}</ExpandableCopy>
                    </Description>
                  )}
                  <Price>
                    {$}
                    {p.base_price
                      ? Number(p.base_price.price).toLocaleString()
                      : '0'}
                    {p.base_price ? `/${p.base_price.unit}` : '0'}
                    {p.price_variation_day && (
                      <Variation>Varies by day</Variation>
                    )}
                    {p.price_variation_season && (
                      <Variation>Varies by season</Variation>
                    )}
                  </Price>
                </Card>
              );
            })}
          </Grid>
        </GridWrapper>
        {!isScrollable && total > maxOnScreen && currentPos > 0 && (
          <PrevNextBtn
            type="prev"
            onClick={() => setCurrentPos(currentPos - 1)}
          />
        )}
        {!isScrollable &&
          total > maxOnScreen &&
          currentPos < total - maxOnScreen && (
            <PrevNextBtn
              type="next"
              onClick={() => setCurrentPos(currentPos + 1)}
            />
          )}
      </Wrapper>
    </VendorProfileSection>
  );
}
